import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api.js';
import { getTripIdentity, rememberVisit } from './identity.js';

/**
 * Loads one outing and keeps it live.
 *
 * Several members open the same link and edit it at the same time, so the
 * server pushes the whole updated document over Server-Sent Events. Rendering
 * from a server-computed projection (seat counts included) means two browsers
 * can never disagree about how many seats are left.
 */
export function useTrip(shareToken) {
  const [trip, setTrip] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);
  const [identity, setIdentity] = useState(() => getTripIdentity(shareToken));

  const refreshIdentity = useCallback(() => setIdentity(getTripIdentity(shareToken)), [shareToken]);

  // Guards against a late response from a previous token overwriting the current one.
  const tokenRef = useRef(shareToken);
  tokenRef.current = shareToken;

  useEffect(() => {
    if (!shareToken) return undefined;
    const controller = new AbortController();
    setStatus('loading');
    setError(null);

    api
      .getTrip(shareToken, getTripIdentity(shareToken).adminToken, controller.signal)
      .then(({ trip: loaded }) => {
        if (tokenRef.current !== shareToken) return;
        setTrip(loaded);
        setStatus('ready');
        rememberVisit(shareToken, loaded.title);
      })
      .catch((err) => {
        if (controller.signal.aborted || tokenRef.current !== shareToken) return;
        setError(err instanceof ApiError ? err : new Error('Connexion impossible.'));
        setStatus('error');
      });

    return () => controller.abort();
  }, [shareToken]);

  // Live channel. EventSource reconnects by itself; we only mirror its state.
  // Opened only once the first load succeeded: pointing EventSource at a bad
  // token would make the browser retry the 404 every few seconds forever.
  const liveReady = Boolean(shareToken) && status === 'ready';
  useEffect(() => {
    if (!liveReady) return undefined;
    const source = new EventSource(`/api/trips/${shareToken}/events`);

    source.addEventListener('open', () => setLive(true));
    source.addEventListener('trip', (event) => {
      try {
        const next = JSON.parse(event.data);
        // Ignore frames that arrive out of order after a local write.
        setTrip((prev) => (prev && next.version < prev.version ? prev : next));
        setStatus('ready');
      } catch {
        /* malformed frame: the next one will correct us */
      }
    });
    source.addEventListener('deleted', () => {
      setError(new Error('Cette sortie a ete supprimee par son organisateur.'));
      setStatus('error');
      source.close();
    });
    source.addEventListener('error', () => setLive(false));

    return () => {
      setLive(false);
      source.close();
    };
  }, [shareToken, liveReady]);

  /** Apply a server response locally right away, rather than waiting for the echo. */
  const applyTrip = useCallback((next) => {
    if (next) setTrip((prev) => (prev && next.version < prev.version ? prev : next));
  }, []);

  return { trip, status, error, live, identity, refreshIdentity, applyTrip };
}
